import { useEffect, useMemo } from 'react';

import { RovoChatContextType } from 'src/app/nav-system/nav-system';
import { useUserWorkspacePref } from 'src/hooks/use-pref';
import { useRovoEnabledSitesForWorkspace } from 'src/hooks/use-rovo-enabled-sites';
import { useWorkspaceAiPreferences } from 'src/sections/workspace/sections/settings/components/atlassian-intelligence/atlassian-intelligence';

type Options = {
  setChatState: RovoChatContextType['setChatState'];
  workspaceUuid: string;
};

export const useInitializeRovoChatState = ({
  setChatState,
  workspaceUuid,
}: Options) => {
  const { aiRovoChatEnabled: isRovoChatEnabledForWorkspace } =
    useWorkspaceAiPreferences();

  const [sitePref, updateSitePref, isSitePrefLoading] = useUserWorkspacePref(
    'conversation-assistant-preferred-site',
    workspaceUuid
  );

  const { rovoEnabledSites } = useRovoEnabledSitesForWorkspace(
    workspaceUuid,
    isRovoChatEnabledForWorkspace
  );
  const rovoEnabledSitesUserHasAccessTo = useMemo(
    () => rovoEnabledSites.filter(site => site.userHasAiFeatureAccess),
    [rovoEnabledSites]
  );

  useEffect(
    // Update isEnabled and the siteId based on the API response and user pref
    () => {
      let siteId = '';
      if (!isSitePrefLoading) {
        if (
          sitePref &&
          rovoEnabledSitesUserHasAccessTo.find(
            site => site.cloudId === sitePref
          )
        ) {
          siteId = sitePref;
        } else {
          siteId = rovoEnabledSitesUserHasAccessTo[0]?.cloudId || '';
          if (siteId) {
            updateSitePref(siteId);
          }
        }
      }
      setChatState({
        isEnabled:
          isRovoChatEnabledForWorkspace &&
          rovoEnabledSitesUserHasAccessTo.length > 0,
        siteId,
      });
    },
    [
      isSitePrefLoading,
      rovoEnabledSitesUserHasAccessTo,
      setChatState,
      sitePref,
      updateSitePref,
      isRovoChatEnabledForWorkspace,
    ]
  );

  return {
    rovoEnabledSitesUserHasAccessTo,
    updateSitePref,
  };
};
